import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { getOrgConfig } from '../../utils/orgResolution';
import useSencoPupils from '../../hooks/useSencoPupils';
import { useRmsState } from '../../context/RmsStateContext';
import { apiConfirmSubmission } from '../../utils/rmsApi';
import {
  canConfirmSubmission,
  isReferralPathwayActive,
  isRoleSubmissionConfirmed,
} from '../../utils/submissionConfirmation';
import { TRIAGE_FORM_KEYS } from '../../utils/referralStatus';
import { harbourFormKeysFor, harbourFormTitle } from '../../utils/harbourForms';
import { buildFamilyChild, childFirstName, familyUserName } from '../../utils/familyPortal';
import FamilyPortalShell from './FamilyPortalShell';
import FamilyChildCard from './FamilyChildCard';
import useIsDesktop from './useIsDesktop';
import HarbourButton from '../../components/harbour/HarbourButton';
import HarbourFlyout from '../../components/harbour/HarbourFlyout';
import HarbourNotice from '../../components/harbour/HarbourNotice';
import HarbourPathwayMark from '../../components/harbour/HarbourPathwayMark';
import ConfirmAndSubmitScreen from '../../components/ConfirmAndSubmitScreen';
import '../../components/harbour/harbour.css';

/*
 * Parent or carer "Confirm and submit" page (TDI-415).
 * Route: /rms/family/child/:patientRecordId/confirm/:pathway ('sen' | 'referral').
 *
 * One pathway's final "I have finished" moment, for a parent or carer who
 * arrives by this route. Confirming locks their answers on that pathway only
 * (TDI-651) and, on the referral pathway, submits the referral once the
 * school has also confirmed (TDI-418); on the SEND pathway the profile is
 * made once both have. The forms homepage carries the drawn submit pop-up
 * ("Submit your forms · Parent or carer") and is where Your child sends them.
 * The chrome mirrors FamilyConsentPage: a plain shell page on mobile, the
 * wide task flyout over Your children on desktop.
 */
export default function FamilyConfirmPage() {
  const navigate = useNavigate();
  const { patientRecordId, pathway } = useParams();
  const { user, claims } = useAuth();
  const claimsOrgId = claims?.rmsOrgId;
  const orgId = claimsOrgId || getOrgConfig().orgId;
  const { pupils, loading, error, reload } = useSencoPupils({ orgId, claimsOrgId });
  const isDesktop = useIsDesktop();

  const [busy, setBusy] = useState(false);
  const [confirmFailed, setConfirmFailed] = useState(false);

  const childUrl = `/rms/family/child/${patientRecordId}`;
  const formsUrl = `${childUrl}/forms/${pathway}`;

  const pupil = (pupils || []).find((p) => p.id === patientRecordId) || null;
  const patient = pupil?.patient || {};
  const formProgress = pupil?.formProgress || {};
  const firstName = childFirstName(pupil) || 'your child';

  const { ensurePreSchema } = useRmsState();

  // The forms this pathway's confirmation covers, in journey order — the
  // same ones its forms homepage lists. Each pathway is confirmed on its own
  // and locks only its own forms (TDI-651, Tom King 27 Oct: the SEND pathway
  // and the referral are completely separate), so the referral's page never
  // lists the strengths and needs forms, nor the SEND page the referral's.
  const formKeys = useMemo(() => {
    if (!pupil) return [];
    const record = pupil.patient || null;
    if (pathway === 'referral') {
      if (!isReferralPathwayActive(record)) return [];
      const opts = {
        patient: record,
        intakeConcern: record?.intakeConcern || null,
        senActive: Boolean(pupil.sen && pupil.sen.stage !== 'stopped'),
        schoolPhase: record?.schoolPhase || null,
      };
      return [...harbourFormKeysFor('referral', opts), ...TRIAGE_FORM_KEYS];
    }
    return harbourFormKeysFor('sen', { patient: record });
  }, [pupil, pathway]);

  // The live schemas carry the titles the forms homepage shows; without them
  // the list fell back to the instruments' domain names.
  const [schemas, setSchemas] = useState({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(formKeys.map(async (formKey) => {
        try {
          return [formKey, await ensurePreSchema({ orgId, formKey })];
        } catch (_) {
          return [formKey, null];
        }
      }));
      if (!cancelled) setSchemas(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
  }, [formKeys, ensurePreSchema, orgId]);
  const formTitles = useMemo(
    () => formKeys.map((key) => harbourFormTitle(key, schemas[key] || null)),
    [formKeys, schemas]
  );

  // Only the child's parent or carer confirms here (the school confirms on
  // the SENCO portal); anyone else is sent to their own dashboard.
  if (claims && claims.userType !== 'RMS Legal Guardian') {
    return <Navigate to="/rms/dashboard" replace />;
  }
  if (pathway !== 'sen' && pathway !== 'referral') {
    return <Navigate to={childUrl} replace />;
  }

  const userName = familyUserName(user, pupils) || 'Account';

  const shell = (children) => (
    <FamilyPortalShell userName={userName} back={{ to: formsUrl, label: 'Back' }}>
      {children}
    </FamilyPortalShell>
  );

  if (loading) {
    return shell(
      <p className="harbour-body-16" role="status">Loading…</p>
    );
  }

  if (!pupil) {
    return shell(
      <div className="flex flex-col gap-4">
        <HarbourNotice kind="error" title="We could not load this page">
          {error || 'Check your internet connection and try again.'}
        </HarbourNotice>
        <HarbourButton kind="secondary" onClick={() => reload({ force: true })}>
          Try again
        </HarbourButton>
      </div>
    );
  }

  // Already confirmed this pathway: nothing more to do here.
  if (isRoleSubmissionConfirmed(patient, 'guardian', pathway)) {
    return <Navigate to={childUrl} replace />;
  }
  // Forms on this pathway still outstanding: its homepage is the honest
  // place to be.
  if (!canConfirmSubmission({ patient, formProgress, role: 'guardian', pathway })) {
    return <Navigate to={formsUrl} replace />;
  }

  const statementDetail = pathway === 'referral'
    ? 'The referral is sent to the clinical team once you and the school have both confirmed.'
    : `When you and the school have both confirmed, you can both see ${firstName}’s profile, with home and school side by side.`;

  const onConfirm = async () => {
    setBusy(true);
    setConfirmFailed(false);
    try {
      await apiConfirmSubmission({ orgId, patientRecordId, pathway });
      reload({ force: true });
      navigate(childUrl);
    } catch (_e) {
      setBusy(false);
      setConfirmFailed(true);
    }
  };

  const screen = (
    <ConfirmAndSubmitScreen
      formTitles={formTitles}
      statementDetail={statementDetail}
      busy={busy}
      error={confirmFailed}
      onConfirm={onConfirm}
      laterTo={formsUrl}
    />
  );

  // Desktop: the task flyout over Your children (mirrors FamilyConsentPage).
  if (isDesktop) {
    const allChildren = (pupils || []).map(buildFamilyChild).filter(Boolean);
    return (
      <FamilyPortalShell userName={userName} maxWidth="full">
        <div className="family-page-head">
          <h1 className="family-page-head__title family-page-head__title--home">Your children</h1>
          <p className="family-page-head__intro">Choose your child’s name to see their details and questions.</p>
        </div>
        <ul className="family-child-cards">
          {allChildren.map((c) => (
            <li key={c.id}><FamilyChildCard child={c} linkState={{ stay: true }} /></li>
          ))}
        </ul>

        <HarbourFlyout
          open
          width="wide"
          title={pupil.name}
          year={pupil.yearGroup}
          eyebrow={{ tone: pathway, label: pathway === 'sen' ? 'SEND pathway' : 'Referral pathway' }}
          onClose={() => { if (!busy) navigate(formsUrl, { state: { stay: true } }); }}
          closeLabel="Close"
        >
          <div className="harbour-task-body">
            <div className="harbour-task-body__steps" aria-hidden="true" />
            <div className="harbour-task-body__main">{screen}</div>
          </div>
        </HarbourFlyout>
      </FamilyPortalShell>
    );
  }

  // Mobile: the plain shell page.
  return shell(
    <div className="flex flex-col gap-4">
      <p className="ffh-eyebrow">
        <HarbourPathwayMark pathway={pathway} />
        <span>{pathway === 'sen' ? 'SEND pathway' : 'Referral pathway'}</span>
      </p>
      {screen}
    </div>
  );
}
